import { AxiosInstance } from "axios";
import {
  TaskSummary,
  TaskDetails,
  CreateTaskRequest,
  UpdateTaskRequest,
  UpdateTaskScheduleRequest,
  SourceTask,
  Numbers,
  HistoricNumbers,
  BoardTasksData,
  StatusTaskHistory,
  SubTask,
  AddSubTaskResult,
} from "../types";

/**
 * Task API endpoints
 * Platform-agnostic - works with any HTTP client
 *
 * All endpoints are relative to the API base (e.g., /api/tasks)
 *
 * Note: Use TaskSummary for board display/columns, TaskDetails for detailed dialogs
 */
export class TasksApi {
  constructor(private client: AxiosInstance) {}

  private ensureBoardId(boardId?: string): string {
    if (!boardId) {
      throw new Error("boardId is required for task operations");
    }

    return boardId;
  }

  /**
   * Get all tasks for a board (lightweight summaries for board display)
   */
  async getTasks(boardId: string): Promise<TaskSummary[]> {
    const response = await this.client.get<{
      tasks: TaskSummary[];
      statuses: unknown[];
    }>(`/api/boards/${boardId}/tasks`);
    return response.data.tasks || [];
  }

  /**
   * Get all tasks and statuses for a board in a single call
   * This is more efficient than calling getTasks() and getStatuses() separately
   * The server has already calculated the ordering, so we don't need to re-sort in React
   */
  async getBoardTasksWithStatuses(boardId: string): Promise<BoardTasksData> {
    const response = await this.client.get<BoardTasksData>(
      `/api/boards/${boardId}/tasks`,
    );

    return response.data;
  }

  /**
   * Create a new task (returns summary for board display)
   */
  async createTask(
    request: CreateTaskRequest,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const boardId = this.ensureBoardId(request.boardId);
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks`;
    const response = await this.client.post<TaskSummary>(url, request);
    return response.data;
  }

  /**
   * Update an existing task (returns summary for board display)
   */
  async updateTask(
    boardId: string,
    taskId: string,
    request: UpdateTaskRequest,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks/${taskId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks/${taskId}`;
    const response = await this.client.put<TaskSummary>(url, request);
    return response.data;
  }

  /**
   * Delete a task
   */
  async deleteTask(
    boardId: string,
    taskId: string,
    hubConnectionId?: string,
  ): Promise<void> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks/${taskId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks/${taskId}`;
    await this.client.delete(url);
  }

  /**
   * Move task to different status
   */
  async moveTask(
    boardId: string,
    taskId: string,
    statusId: string,
    order?: number,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    return this.updateTaskStatus(
      boardId,
      taskId,
      statusId,
      order,
      hubConnectionId,
    );
  }

  /**
   * Archive a task
   */
  async archiveTask(
    boardId: string,
    taskId: string,
    hubConnectionId?: string,
  ): Promise<void> {
    return this.setTaskArchived(boardId, taskId, true, hubConnectionId);
  }

  /**
   * Unarchive a task
   */
  async unarchiveTask(
    boardId: string,
    taskId: string,
    hubConnectionId?: string,
  ): Promise<void> {
    return this.setTaskArchived(boardId, taskId, false, hubConnectionId);
  }

  /**
   * Update only a task's status and order
   */
  async updateTaskStatus(
    boardId: string,
    taskId: string,
    statusId: string,
    order?: number,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks/${taskId}/status?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks/${taskId}/status`;
    const response = await this.client.put<TaskSummary>(url, {
      statusId,
      order,
    });
    return response.data;
  }

  /**
   * Update only a task's scheduled day, time of day and duration.
   *
   * Deliberately narrow: the schedule view holds task summaries, which carry no
   * notes or sub-tasks, so it must not send a whole update request.
   *
   * @param request Pass `scheduledDate: null` to unschedule the task entirely.
   */
  async updateTaskSchedule(
    boardId: string,
    taskId: string,
    request: UpdateTaskScheduleRequest,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks/${taskId}/schedule?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks/${taskId}/schedule`;
    const response = await this.client.put<TaskSummary>(url, request);
    return response.data;
  }

  /**
   * Set task done state
   */
  async setTaskDone(
    boardId: string,
    taskId: string,
    isDone: boolean,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks/${taskId}/isdone?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks/${taskId}/isdone`;
    const response = await this.client.put<TaskSummary>(url, {
      isTrue: isDone,
    });
    return response.data;
  }

  /**
   * Get a task's subtasks, in order. Lighter than getTaskDetails: no comments or history.
   */
  async getSubTasks(boardId: string, taskId: string): Promise<SubTask[]> {
    const response = await this.client.get<SubTask[]>(
      `/api/boards/${encodeURIComponent(boardId)}/tasks/${encodeURIComponent(taskId)}/subtasks`,
    );
    return response.data;
  }

  /**
   * Tick or untick one subtask, leaving the rest of the task as it is.
   * Returns the task summary so the card's counts can be refreshed.
   */
  async setSubTaskDone(
    boardId: string,
    taskId: string,
    subTaskId: string,
    isDone: boolean,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const base = `/api/boards/${encodeURIComponent(boardId)}/tasks/${encodeURIComponent(taskId)}/subtasks/${encodeURIComponent(subTaskId)}/isdone`;
    const url = hubConnectionId
      ? `${base}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : base;
    const response = await this.client.put<TaskSummary>(url, {
      isTrue: isDone,
    });
    return response.data;
  }

  /**
   * Append a subtask to a task, leaving the rest of the task as it is.
   */
  async addSubTask(
    boardId: string,
    taskId: string,
    title: string,
    hubConnectionId?: string,
  ): Promise<AddSubTaskResult> {
    const base = `/api/boards/${encodeURIComponent(boardId)}/tasks/${encodeURIComponent(taskId)}/subtasks`;
    const url = hubConnectionId
      ? `${base}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : base;
    const response = await this.client.post<AddSubTaskResult>(url, { title });
    return response.data;
  }

  /**
   * Remove one subtask, leaving the rest of the task as it is.
   * Returns the task summary so the card's counts can be refreshed.
   */
  async deleteSubTask(
    boardId: string,
    taskId: string,
    subTaskId: string,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const base = `/api/boards/${encodeURIComponent(boardId)}/tasks/${encodeURIComponent(taskId)}/subtasks/${encodeURIComponent(subTaskId)}`;
    const url = hubConnectionId
      ? `${base}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : base;
    const response = await this.client.delete<TaskSummary>(url);
    return response.data;
  }

  /**
   * Archive or unarchive task
   */
  async setTaskArchived(
    boardId: string,
    taskId: string,
    archived: boolean,
    hubConnectionId?: string,
  ): Promise<void> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks/${taskId}/archive?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks/${taskId}/archive`;
    await this.client.put(url, { isTrue: archived });
  }

  /**
   * Find connection/source tasks to link to a task
   */
  async findSourceTasks(
    boardId: string,
    excludeTaskId: string,
    searchTerm: string,
  ): Promise<SourceTask[]> {
    const response = await this.client.get<SourceTask[]>(
      `/api/boards/${boardId}/tasks/findsourcetasks?excludeTaskId=${encodeURIComponent(excludeTaskId)}&searchTerm=${encodeURIComponent(searchTerm)}`,
    );
    return response.data;
  }

  /**
   * Move a source task onto a task, taking it off whichever task currently owns it.
   *
   * Connecting two cards is not a task update: it rewrites which task owns a source
   * task and archives the old one when that empties it, so it has its own endpoint
   * rather than riding on a full task replace.
   *
   * @param boardId - Board holding both tasks
   * @param taskId - Task the source task is moving to
   * @param sourceTaskId - Source task being moved
   * @param hubConnectionId - SignalR connection to leave out of the broadcast
   * @returns The updated target task
   */
  async moveSourceTask(
    boardId: string,
    taskId: string,
    sourceTaskId: string,
    hubConnectionId?: string,
  ): Promise<TaskSummary> {
    const url = `/api/boards/${encodeURIComponent(boardId)}/tasks/${encodeURIComponent(taskId)}/sourcetasks/${encodeURIComponent(sourceTaskId)}`;
    const response = await this.client.put<TaskSummary>(
      hubConnectionId
        ? `${url}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
        : url,
    );
    return response.data;
  }

  /**
   * Get archived (hidden) tasks for a board with pagination (returns summaries for list display)
   */
  async getArchivedTasks(
    boardId: string,
    continuationToken: string = "",
    pageSize: number = 100,
  ): Promise<{ items: TaskSummary[]; nextContinuationToken?: string }> {
    const response = await this.client.get<{
      items?: TaskSummary[];
      nextContinuationToken?: string;
    }>(
      `/api/boards/${boardId}/tasks/archive?continuationToken=${encodeURIComponent(continuationToken)}&pageSize=${pageSize}`,
    );
    return {
      items: response.data.items || [],
      nextContinuationToken: response.data.nextContinuationToken,
    };
  }

  /**
   * Get detailed information for a specific task within a board (full details for editing)
   */
  async getTaskDetails(boardId: string, taskId: string): Promise<TaskDetails> {
    const response = await this.client.get<TaskDetails>(
      `/api/boards/${boardId}/tasks/${taskId}`,
    );
    return response.data;
  }

  /**
   * Update sort order for tasks in a status
   */
  async updateTaskSortOrder(
    boardId: string,
    statusUpdates: Array<{ statusId: string; taskIds: string[] }>,
    hubConnectionId?: string,
  ): Promise<void> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/tasks?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/tasks`;
    await this.client.put(url, { statuses: statusUpdates });
  }

  /**
   * Get live statistics for a board
   */
  async getLiveNumbers(
    boardId: string,
    connectionId?: string | null,
  ): Promise<Numbers> {
    const normalizedConnectionId = connectionId?.trim();
    const query = normalizedConnectionId
      ? `?connectionId=${encodeURIComponent(normalizedConnectionId)}`
      : "";
    const response = await this.client.get<Numbers>(
      `/api/boards/${boardId}/tasks/livenumbers${query}`,
    );
    return response.data;
  }

  /**
   * Get historic statistics for a board.
   *
   * @param boardId The board to report on.
   * @param connectionId Limit to a single connection, or null for all of them.
   * @param fromDate Only count tasks completed on or after this instant. Omit for
   *   the board's whole history -- on a long-lived board that is the expensive
   *   case, so callers should pass a window unless the user asked for all of it.
   * @param toDate Only count tasks completed strictly before this instant.
   */
  async getHistoricNumbers(
    boardId: string,
    connectionId?: string | null,
    fromDate?: Date | null,
    toDate?: Date | null,
  ): Promise<HistoricNumbers> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const queryParts = [`timeZoneIANA=${encodeURIComponent(timeZone)}`];
    const normalizedConnectionId = connectionId?.trim();
    if (normalizedConnectionId) {
      queryParts.unshift(
        `connectionId=${encodeURIComponent(normalizedConnectionId)}`,
      );
    }
    if (fromDate) {
      queryParts.push(`fromDate=${encodeURIComponent(fromDate.toISOString())}`);
    }
    if (toDate) {
      queryParts.push(`toDate=${encodeURIComponent(toDate.toISOString())}`);
    }
    const response = await this.client.get<HistoricNumbers>(
      `/api/boards/${boardId}/tasks/historicnumbers?${queryParts.join("&")}`,
    );
    return response.data;
  }

  /**
   * Get task history for a specific status over a date range
   */
  async getStatusTaskHistory(
    boardId: string,
    statusId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<StatusTaskHistory[]> {
    const response = await this.client.get<StatusTaskHistory[]>(
      `/api/boards/${boardId}/tasks/getstatustaskhistory?statusId=${encodeURIComponent(statusId)}&startDate=${encodeURIComponent(startDate.toISOString())}&endDate=${encodeURIComponent(endDate.toISOString())}`,
    );
    return response.data;
  }
}
